import { Button } from "@mantine/core";
import { showNotification } from "@mantine/notifications";
import { IconRotateClockwise } from "@tabler/icons-react";
import { MutationOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { FirestoreError } from "firebase/firestore";
import { QUERY_KEY, continueComiteDeInovacao } from "@/modules/comite-inovacao/firestore";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";

type TContinueComiteDeInovacaoResponse = Awaited<ReturnType<typeof continueComiteDeInovacao>>;
type TContinueComiteDeInovacaoMutationOptions = Omit<
  MutationOptions<TContinueComiteDeInovacaoResponse, FirestoreError>,
  "mutationFn"
>;
/**
 * Continue comite de inovação mutation hook
 * @param comite - Comite de inovação
 * @param [options] = {} - Mutation options
 * @returns Continue comite de inovação mutation
 */
function useContinueComiteInovacaoMutation(
  comite: TComiteInovacao,
  options: TContinueComiteDeInovacaoMutationOptions = {}
) {
  const activeEmpresaId = useUserStore((store) => store.activeEmpresaId);

  return useMutation<TContinueComiteDeInovacaoResponse, FirestoreError>({
    mutationFn() {
      if (!activeEmpresaId) {
        throw new Error("Empresa não selecionada");
      }

      return continueComiteDeInovacao(activeEmpresaId, comite.id);
    },
    ...options
  });
}

const ContinuarComiteInovacaoButton = ({ comite }: { comite: TComiteInovacao }) => {
  const queryClient = useQueryClient();
  const activeEmpresaId = useUserStore((store) => store.activeEmpresaId);

  const { mutate, isPending } = useContinueComiteInovacaoMutation(comite, {
    onSuccess() {
      showNotification({
        color: "green",
        message: "Comitê retornado com sucesso!",
        autoClose: 3500
      });

      // eslint-disable-next-line compat/compat
      return Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY]
        }),
        queryClient.invalidateQueries({
          queryKey: ["empresas", { id: activeEmpresaId }, "comitesInovacao", "ativo"]
        }),
        queryClient.invalidateQueries({
          queryKey: ["empresas", { id: activeEmpresaId }, "comitesInovacao", { id: comite.id }]
        })
      ]);
    },
    onError(error) {
      captureException(error, true);
    }
  });

  /**
   * Handle click on button to continue comite de inovação
   */
  function onClick() {
    mutate();
  }

  return (
    <Button leftSection={<IconRotateClockwise />} onClick={onClick} loading={isPending}>
      Retornar
    </Button>
  );
};

export default ContinuarComiteInovacaoButton;
